import type { RefObject } from 'react';
import { useEffect, useState } from 'react';
import { useIsDesktop } from '../../hooks/useMediaQuery';

interface EmpathyUnlockCelebrationProps {
  isActive: boolean;
  onComplete: () => void;
  /** Optional ref to the target element (E button) - if not provided, defaults to bottom nav position */
  targetRef?: RefObject<HTMLElement>;
  /** If true, skip playing the choir sound (already pre-played from user gesture context) */
  skipSound?: boolean;
}

/** Animation phase timing constants (in milliseconds) */
const TIMING = {
  /** Phase 2: Burst effect starts */
  BURST: 500,
  /** Phase 3: Glow effect starts */
  GLOW: 700,
  /** Phase 4: Text appears */
  TEXT: 1000,
  /** Phase 5: Animation completes */
  COMPLETE: 4500,
} as const;

/** Audio configuration */
const AUDIO_SRC = '/choir.opus';

/**
 * EmpathyUnlockCelebration Component
 *
 * A full-screen celebration animation that plays when the user unlocks
 * empathy on-demand features by completing the intro domain.
 *
 * Animation sequence:
 * 1. Screen dims, E button area comes into focus
 * 2. Lock icon "pops" off with a bounce
 * 3. Warm sunshine rays pulse outward gently
 * 4. Mystical "ahhh" sound plays
 * 5. Glowing aura pulses around the button
 * 6. "Introduction complete" text appears (not "unlocked": since P5 the
 *    intro gates nothing — COHORT-COMMUNICATION.md X1)
 * 7. Animation completes, callback fires
 */
export default function EmpathyUnlockCelebration({
  isActive,
  onComplete,
  targetRef,
  skipSound = false,
}: EmpathyUnlockCelebrationProps) {
  const isDesktop = useIsDesktop();
  const [phase, setPhase] = useState<'idle' | 'focus' | 'burst' | 'glow' | 'text' | 'complete'>(
    'idle',
  );
  const [targetPosition, setTargetPosition] = useState<{ x: number; y: number } | null>(null);

  // Calculate target position from ref, or auto-detect from the correct button in DOM
  useEffect(() => {
    if (!isActive) return;

    if (targetRef?.current) {
      const rect = targetRef.current.getBoundingClientRect();
      setTargetPosition({
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2,
      });
      return;
    }

    // No ref provided — find the appropriate button in the DOM
    // Desktop: target the Empathy nav button; Mobile: target the E button in bottom nav
    const selector = isDesktop
      ? '[data-onboarding="nav-empathy"]'
      : '[data-onboarding="empathy-e-button"]';
    const btn = document.querySelector(selector);
    if (btn) {
      const rect = btn.getBoundingClientRect();
      setTargetPosition({
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2,
      });
      return;
    }

    // Fallback: center of viewport
    setTargetPosition(null);
  }, [isActive, targetRef, isDesktop]);

  // Main animation sequence
  useEffect(() => {
    if (!isActive) {
      setPhase('idle');
      return;
    }

    // Start animation sequence
    setPhase('focus');

    const timers: NodeJS.Timeout[] = [];

    // Play the mystical sound (skip if already pre-played from user gesture context)
    if (!skipSound) {
      const audio = new Audio(AUDIO_SRC);
      audio.play().catch((err) => console.log('Audio play failed:', err));
    }

    // Phase 2: Burst - Lock pops off
    timers.push(
      setTimeout(() => {
        setPhase('burst');
      }, TIMING.BURST),
    );

    // Phase 3: Glow - Orange glow emanates
    timers.push(
      setTimeout(() => {
        setPhase('glow');
      }, TIMING.GLOW),
    );

    // Phase 4: Text - "Introduction complete" appears
    timers.push(
      setTimeout(() => {
        setPhase('text');
      }, TIMING.TEXT),
    );

    // Phase 5: Complete - Animation finishes
    timers.push(
      setTimeout(() => {
        setPhase('complete');
        onComplete();
      }, TIMING.COMPLETE),
    );

    return () => {
      timers.forEach(clearTimeout);
    };
  }, [isActive, onComplete, skipSound]);

  if (!isActive && phase === 'idle') return null;

  return (
    <div
      className={`fixed inset-0 z-[100] pointer-events-none transition-opacity duration-500 ${
        phase === 'complete' ? 'opacity-0' : 'opacity-100'
      }`}
    >
      {/* Blurred warm backdrop: the redesign canvas, as behind the cards
          (--ee-celebration-veil: the light canvas in light, the dark canvas
          in dark, so the cream ink of dark reads on it) */}
      <div
        className={`absolute inset-0 bg-(--ee-celebration-veil) backdrop-blur-xl transition-opacity duration-500 ${
          phase === 'focus' || phase === 'burst' || phase === 'glow' || phase === 'text'
            ? 'opacity-100'
            : 'opacity-0'
        }`}
      />

      {/* Spotlight on E button area - positioned at target or viewport center */}
      <div
        className="absolute"
        style={
          targetPosition
            ? {
                left: targetPosition.x,
                top: targetPosition.y,
                transform: 'translate(-50%, -50%)',
              }
            : {
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
              }
        }
      >
        {/* Pulsing glow rings */}
        {(phase === 'glow' || phase === 'text') && (
          <>
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 rounded-full animate-ping-slow bg-[rgba(244,122,32,0.3)]" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 rounded-full animate-ping-slower bg-[rgba(244,122,32,0.2)]" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full animate-ping-slowest bg-[rgba(244,122,32,0.1)]" />
          </>
        )}

        {/* Sunshine rays - soft pulsating light (centered on parent anchor point) */}
        {(phase === 'burst' || phase === 'glow' || phase === 'text') && (
          <>
            {/* Inner warm glow - 96px (w-24) */}
            <div
              className="absolute w-24 h-24 rounded-full animate-sunshine-inner"
              style={{
                top: '-48px',
                left: '-48px',
                background:
                  'radial-gradient(circle, rgba(255, 160, 86, 0.6) 0%, rgba(244, 122, 32, 0.3) 50%, transparent 70%)',
              }}
            />
            {/* Middle expanding ring - 160px (w-40) */}
            <div
              className="absolute w-40 h-40 rounded-full animate-sunshine-mid"
              style={{
                top: '-80px',
                left: '-80px',
                background:
                  'radial-gradient(circle, transparent 30%, rgba(244, 122, 32, 0.25) 50%, transparent 70%)',
              }}
            />
            {/* Outer soft rays - 224px (w-56) */}
            <div
              className="absolute w-56 h-56 rounded-full animate-sunshine-outer"
              style={{
                top: '-112px',
                left: '-112px',
                background:
                  'radial-gradient(circle, transparent 40%, rgba(242, 184, 77, 0.2) 60%, transparent 80%)',
              }}
            />
          </>
        )}

        {/* The E button replica, drawn with the nav's own classes (shell.css)
            so it looks like the button it lands on - always visible */}
        {isDesktop ? (
          /* Desktop: the top bar's active Empathy link */
          <div className="absolute" style={{ top: '-22px', left: '-62px' }}>
            <div className="ee-topbar__link ee-topbar__link--active">
              <span className="ee-topbar__e" aria-hidden="true">
                E
              </span>
              <span>Empathy</span>
            </div>
          </div>
        ) : (
          /* Mobile: the bottom nav's raised E */
          <div className="absolute" style={{ top: '-32px', left: '-32px' }}>
            <div className="ee-bnav__e-ring absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[74px] h-[74px] rounded-full -z-10"></div>
            <div className="ee-bnav__e-face relative w-16 h-16 rounded-full flex items-center justify-center">
              <span className="text-2xl font-bold text-(--ee-on-accent)">E</span>
            </div>
          </div>
        )}
      </div>

      {/* "Introduction complete" text - positioned high on screen */}
      {phase === 'text' && (
        <div
          className="absolute left-0 right-0 text-center animate-celebration-fade-up px-4"
          style={{ top: '25%' }}
        >
          <p
            className="text-sm text-[var(--ee-orange-text)] font-semibold uppercase tracking-[0.14em] mb-2"
            style={{ textShadow: 'var(--ee-celebration-halo)' }}
          >
            Congratulations!
          </p>
          <p
            className="font-[family-name:var(--ee-font-heading)] text-3xl font-bold text-[var(--ee-navy)]"
            style={{ textShadow: 'var(--ee-celebration-halo-strong)' }}
          >
            Introduction complete
          </p>
        </div>
      )}
    </div>
  );
}
